import { useCallback, useEffect } from 'react'
import { IconTruckDelivery, IconTruck } from '@tabler/icons-react'
import classNames from 'classnames'
import { LoadingSpinner } from '~/components'
import Typography, {
  TypographyVariant,
  TypographyTag,
} from '~/components/Typography'
import { generateDeliveryInfo } from '~/helpers/deliveryHelpers'
import {
  useUserDeliveryPostcode,
  usePage,
  useSession,
  useUserPreferredStore,
} from '~/hooks'
import { LoadState, Product, ShippingMethodCode } from '~/types'
import styles from './ProductDashboardShippingMethods.module.scss'

type DeliveryMethodSectionProps = {
  product: Product
}

type PostcodeChangeData = {
  postcode: string
  city?: string
  state?: string
}

export const DeliveryMethodSection = ({
  product,
}: DeliveryMethodSectionProps) => {
  const { sku, finalPrice } = product
  const productPrice = finalPrice?.centAmount ? finalPrice.centAmount / 100 : 0

  const {
    promptUserPostcodeDrawer,
    findDeliveryOptionsNoPrompt,
    deliveryMethods,
    freightServiceMessage,
    freightServiceErrorCode,
    postcode,
    city,
    state,
    loadState,
  } = useUserDeliveryPostcode()

  const { preferredStoreIsOpen } = useUserPreferredStore()

  const { countryCode } = usePage()
  const { session, status } = useSession()

  const handleChangePostcode = useCallback(
    (value: PostcodeChangeData) => {
      findDeliveryOptionsNoPrompt(sku, productPrice)

      session.token.deliveryPostcode = value.postcode
      session.token.deliveryCity = value.city
      session.token.deliveryState = value.state
    },
    [findDeliveryOptionsNoPrompt, sku, productPrice, session?.token]
  )

  useEffect(() => {
    if (
      postcode &&
      deliveryMethods?.length === 0 &&
      !freightServiceErrorCode &&
      !preferredStoreIsOpen
    ) {
      handleChangePostcode({
        postcode,
        city,
        state,
      })
    }
  }, [
    city,
    deliveryMethods,
    freightServiceErrorCode,
    handleChangePostcode,
    postcode,
    state,
    preferredStoreIsOpen,
  ])

  const openPostcodeDrawer = () => {
    promptUserPostcodeDrawer(sku, productPrice)
  }

  const renderDeliveryIcon = (deliveryMethod: string) => {
    if (
      deliveryMethod === ShippingMethodCode.EXPRESS ||
      deliveryMethod === ShippingMethodCode.SAME_DAY ||
      deliveryMethod === ShippingMethodCode.EXPRESS_JNZ ||
      deliveryMethod === ShippingMethodCode.SAME_DAY_JNZ ||
      deliveryMethod === ShippingMethodCode.EXPRESS_RTM ||
      deliveryMethod === ShippingMethodCode.SAME_DAY_RTM
    ) {
      return <IconTruckDelivery size={28} color='var(--color-pdp-primary)' />
    }

    return <IconTruck size={28} color='var(--color-pdp-primary)' />
  }

  const renderDeliveryMethods = () => {
    return (
      <>
        {deliveryMethods?.map((method) => {
          const deliveryInfo = generateDeliveryInfo(method, countryCode)

          return (
            <button
              type='button'
              key={method.deliveryMethod}
              className={classNames(
                styles.shippingMethod,
                styles.noActiveOutline
              )}
              onClick={openPostcodeDrawer}>
              <div>{renderDeliveryIcon(method.deliveryMethod)}</div>
              <div className={classNames(styles.statusDot, styles.available)} />
              <div className={styles.shippingMethodDetails}>
                <div className={styles.shippingMethodInfo}>
                  <Typography
                    className={styles.shippingMethodName}
                    variant={TypographyVariant.BodySmallBold}
                    tag={TypographyTag.div}>
                    {deliveryInfo.deliveryLabel}
                    {deliveryInfo.isFastest && (
                      <Typography
                        className={styles.fastestBadge}
                        variant={TypographyVariant.BodySmallBold}
                        tag={TypographyTag.div}>
                        Fastest
                      </Typography>
                    )}
                  </Typography>
                  <Typography
                    className={styles.shippingMethodDescription}
                    variant={TypographyVariant.BodySmall}
                    tag={TypographyTag.div}>
                    {deliveryInfo.deliveryDescription}
                  </Typography>
                </div>
              </div>
              <Typography
                className={styles.shippingMethodPrice}
                variant={TypographyVariant.BodyRegularBold}
                tag={TypographyTag.div}>
                ${deliveryInfo.price.toFixed(2)}
              </Typography>
            </button>
          )
        })}
      </>
    )
  }

  const renderNoDeliveryMethods = () => {
    return (
      <button
        type='button'
        className={classNames(styles.shippingMethod, styles.noActiveOutline)}
        onClick={openPostcodeDrawer}>
        <div>{renderDeliveryIcon(ShippingMethodCode.STANDARD)}</div>
        <div className={styles.statusDot} />
        <div className={styles.shippingMethodDetails}>
          <div className={styles.shippingMethodInfo}>
            <Typography
              className={styles.shippingMethodName}
              variant={TypographyVariant.BodySmallBold}
              tag={TypographyTag.div}>
              Delivery Information
            </Typography>
          </div>
          <div className={styles.shippingMethodControls}>
            <Typography
              className={styles.shippingMethodAction}
              variant={TypographyVariant.BodySmallBold}
              tag={TypographyTag.div}>
              Select a postcode
            </Typography>
          </div>
        </div>
      </button>
    )
  }

  const renderDeliveryLoadingStatus = () => {
    return (
      <div className={styles.shippingMethod}>
        <div>{renderDeliveryIcon(ShippingMethodCode.STANDARD)}</div>
        <div className={styles.statusDot} />
        <div className={styles.shippingMethodDetails}>
          <div className={styles.shippingMethodInfo}>
            <Typography
              className={styles.shippingMethodName}
              variant={TypographyVariant.BodySmallBold}
              tag={TypographyTag.div}>
              Checking delivery options...
            </Typography>
          </div>
        </div>
        <LoadingSpinner size='sm' />
      </div>
    )
  }

  const renderDeliveryMethodsError = () => {
    return (
      <button
        type='button'
        className={classNames(styles.shippingMethod, styles.noActiveOutline)}
        onClick={openPostcodeDrawer}>
        <div>{renderDeliveryIcon(ShippingMethodCode.STANDARD)}</div>
        <div className={styles.statusDot} />
        <div className={styles.shippingMethodDetails}>
          <div className={styles.shippingMethodInfo}>
            <Typography
              className={styles.shippingMethodName}
              variant={TypographyVariant.BodySmallBold}
              tag={TypographyTag.div}>
              {freightServiceMessage || 'Error fetching delivery options'}
            </Typography>
          </div>
          <div className={styles.shippingMethodControls}>
            <Typography
              className={styles.shippingMethodAction}
              variant={TypographyVariant.BodySmallBold}
              tag={TypographyTag.div}>
              Select a postcode
            </Typography>
          </div>
        </div>
      </button>
    )
  }

  if (loadState === LoadState.Loading || status === 'loading') {
    return renderDeliveryLoadingStatus()
  }

  if (freightServiceErrorCode === 0) {
    if (deliveryMethods && deliveryMethods?.length > 0) {
      return renderDeliveryMethods()
    }

    return renderNoDeliveryMethods()
  }

  return renderDeliveryMethodsError()
}
